// CahierLivrable.tsx — CAHIER.DESIGN.1 (S206)
// Visualisation et edition du CPRJ avec design SimAmorcer (hero, sidebar, cards).
// Remplace CahierDocument.tsx dans ConceptionRouter. CahierDocument reste intact.

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Loader2,
  AlertCircle,
  Users,
  ListOrdered,
  BarChart3,
  Menu,
  X,
  Target,
} from "lucide-react";
import { cn } from "../../components/ui/utils";
import { SF, CARD_BASE, CARD_HEADER } from "../core/styles";
import { L2_THEMES, type L2Theme } from "./docforge-config";
import {
  getStructure,
  getValeurs,
  putValeur,
  type CprjStructure,
  type CprjSection,
  type CprjChamp,
  type CprjValeur,
  type CprjParams,
} from "../hooks/useCprj";
import { FieldActionButtons } from "./FieldActionButtons";
import { applyInlineFmt, BlockRenderer, BlockDisplayContext } from "./workspace-block-renderers";
import type { WorkspaceBlock } from "../core/types";
import { useChatContext } from "../../v2/context/ChatContext";
import { injectHeroStyles } from "../sections/shared/LivingHero";
import { useAmorcer } from "../AmorcerContext";
import { SujetBanner } from "./SujetBanner";
import type {
  DiscussionElements,
  PontValeur,
} from "../hooks/useDiscussionElements";
import { ETAPES_DISCUSSION } from "../core/etapes-discussion";

// ── Types ─────────────────────────────────────────────────────────────────────

type Vue = "RP" | "RS" | "DJ" | "SO" | "CC";
type ValeurMap = Map<string, CprjValeur>;

interface Props {
  onBack?: () => void;
  discussionId?: string | null;
  chantierId?: number | null;
  projetId?: number | null;
  missionId?: number | null;
  source?: "cprj" | "discussion";
  discussionElements?: DiscussionElements | null;
  onConfirmerSujet?: () => Promise<{ ok: boolean; status?: number }>;
  onPreciserSujet?: () => void;
  onApprouver?: (id: number) => void;
  onValider?: () => Promise<{ ok: boolean; status?: number }>;
  onRetenirPiste?: (pisteId: number) => void;
  onEpinglerPlan?: (elementId: number) => void;
  onTransformerEnPistes?: (elementIds: number[]) => void;
}

// ── Constantes ────────────────────────────────────────────────────────────────

const VUES: Vue[] = ["RP", "RS", "DJ", "SO", "CC"];

const VUE_LABELS: Record<Vue, string> = {
  RP: "Pre-rapport",
  RS: "Rapport SMART",
  DJ: "Dossier de jumelage",
  SO: "Soumission",
  CC: "Cahier complet",
};

const TYPES_EDITABLES = new Set(["T", "N", "L"]);

// E3 — couleurs cellules chiffres "Ce qu'on sait" (Main.dc.html L29)
const FACT_NUM_COLORS = ["text-orange-600", "text-blue-600", "text-emerald-600"];
// E4 — styles cartes "Points à cerner" (Main.dc.html L31-47), cycle par rang
const Q_CARD_STYLES = [
  { border: "border-orange-300", from: "from-orange-50", hdr: "bg-orange-100/60", badge: "bg-orange-500 text-white", btn: "bg-orange-500 text-white" },
  { border: "border-amber-300", from: "from-amber-50", hdr: "bg-amber-100/60", badge: "bg-amber-500 text-white", btn: "bg-amber-500 text-white" },
  { border: "border-amber-300", from: "from-amber-50", hdr: "bg-amber-100/60", badge: "bg-amber-500 text-white", btn: "bg-amber-500 text-white" },
  { border: "border-emerald-300", from: "from-emerald-50", hdr: "bg-emerald-100/60", badge: "bg-emerald-500 text-white", btn: "bg-emerald-500 text-white" },
];

const THEME: L2Theme = L2_THEMES.amber;

const NIVEAU_COLORS: Record<string, string> = {
  PROPOSE: "bg-amber-100 text-amber-700",
  DEFAUT: "bg-gray-100 text-gray-500",
  DECLARE: "bg-blue-100 text-blue-700",
  CORRIGE: "bg-orange-100 text-orange-700",
  VALIDE: "bg-emerald-100 text-emerald-700",
  APPROUVE: "bg-emerald-100 text-emerald-700",
  ECARTE: "bg-red-100 text-red-700",
  MIS_DE_COTE: "bg-gray-100 text-gray-500",
};

const NIVEAU_BORDER: Record<string, string> = {
  PROPOSE: "border-l-amber-400",
  DEFAUT: "border-l-gray-300",
  DECLARE: "border-l-blue-400",
  CORRIGE: "border-l-orange-400",
  VALIDE: "border-l-emerald-400",
  APPROUVE: "border-l-emerald-400",
  ECARTE: "border-l-red-400",
  MIS_DE_COTE: "border-l-gray-300",
};

const NIVEAU_HEADER_BG: Record<string, string> = {
  PROPOSE: "bg-amber-50",
  DEFAUT: "bg-gray-50",
  DECLARE: "bg-blue-50",
  CORRIGE: "bg-orange-50",
  VALIDE: "bg-emerald-50",
  APPROUVE: "bg-emerald-50",
  ECARTE: "bg-red-50",
  MIS_DE_COTE: "bg-gray-50",
};

// NIVEAU.REGUL.1 — libellés du badge « Source · État » (DEC-BADGE-NIVEAU-1) ;
// mêmes identifiants et libellés que registre/boites.json (contrôle : test_boites_registre.py).
const PROVENANCE_LIBELLES: Record<string, string> = {
  client: "Client",
  profil: "Profil d'entreprise",
  document: "Document téléversé",
  moteur: "Moteur de calcul",
  bot: "Bot",
  expert: "Expert",
  fournisseur: "Fournisseur",
};

const ETAT_LIBELLES: Record<string, string> = {
  proposee: "Proposée",
  acceptee: "Acceptée",
  approuvee: "Approuvée et verrouillée",
  ecartee: "Écartée",
  mise_de_cote: "Mise de côté",
};

const BOT_PRENOM: Record<string, string> = {
  CEOB: "Carlos",
  CPOB: "Paco",
  CTOB: "Tim",
  CFOB: "Frank",
  CMOB: "Mathilde",
  CSOB: "Simone",
  COOB: "Olivier",
  CHROB: "Helene",
  CROB: "Rich",
  CISOB: "Sebastien",
  CLOB: "Loulou",
  CINOB: "Ines",
};

// ── Reverse mapping champ_id → block_type (pour BlockRenderer dispatch) ───────

const CHAMP_TO_BLOCK_TYPE: Record<string, string> = {
  "B-D01.T01": "diagnostic",
  "B-D01.T02": "5pourquoi",
  "B-D01.T03": "etat_des_lieux",
  "B-D01.T04": "metriques",
  "B-D01.T05": "benchmark",
  "B-D02.T01": "brainstorm",
  "B-D02.T02": "recommandations",
  "B-D02.T03": "consultation",
  "B-D03.T01": "challenge",
  "B-D03.T02": "debat",
  "B-D03.T03": "decision",
  "B-D04.T01": "synthese",
  "B-D04.T02": "plan_action",
  "B-D04.T03": "risques",
  "B-D04.T04": "vote",
  "B-D04.T05": "timeline",
};

// ── Mapping étape CREDO → section gabarit ──────────────────────────────────────

const ETAPE_TO_SECTION: Record<string, string> = {
  C: "D00", R: "D01", E: "D02", D: "D03", O: "D04",
};

// ── Conversion pont → ValeurMap (mode discussion) ─────────────────────────────

function _pontToValeurMap(valeurs: PontValeur[] | undefined): ValeurMap {
  const m = new Map<string, CprjValeur>();
  if (!valeurs) return m;
  for (const pv of valeurs) {
    m.set(pv.champ_id, {
      id: pv.greffier_id ?? pv.block_id ?? 0,
      champ_id: pv.champ_id,
      valeur: pv.valeur
        ?? (pv.structured_data != null ? { structured_data: pv.structured_data, bloc_id: pv.block_id } : null)
        ?? (pv.items != null ? { elements: pv.items } : null),
      version: 1,
      niveau: pv.niveau ?? "PROPOSE",
      provenance: pv.provenance ?? "bot",
      etat: (pv.provenance ?? "bot") === "client" ? "acceptee" : "proposee",
      source_bot: pv.source_bot ?? null,
      source_bot_name: pv.source_bot_name ?? null,
      elements: pv.items ?? null,
      block_type: CHAMP_TO_BLOCK_TYPE[pv.champ_id] ?? null,
      block_id: pv.block_id ?? null,
      title: pv.title ?? null,
      summary: pv.summary ?? null,
    } as unknown as CprjValeur);
  }
  return m;
}

// ── Helpers (copies de CahierDocument.tsx L47-96) ─────────────────────────────

function _latestValeurs(list: CprjValeur[]): ValeurMap {
  const m = new Map<string, CprjValeur>();
  for (const v of list) {
    const cur = m.get(v.champ_id);
    if (!cur || v.version > cur.version) m.set(v.champ_id, v);
  }
  return m;
}

function _champRempli(champ: CprjChamp, vm: ValeurMap): boolean {
  const v = vm.get(champ.id);
  if (!v) return false;
  const val = v.valeur;
  if (val === null || val === undefined || val === "") return false;
  if (typeof val === "object" && !Array.isArray(val)) {
    return Object.values(val as Record<string, unknown>).some(
      (x) => x !== null && x !== undefined && x !== "",
    );
  }
  return true;
}

function _extraire(v: CprjValeur | undefined, _type0: string): string {
  if (!v?.valeur) return "";
  const raw = v.valeur;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return String(raw);
  const obj = raw as Record<string, unknown>;
  // Essayer toutes les cles connues, quel que soit le type declare
  if (obj.texte != null && obj.texte !== "") return String(obj.texte);
  if (obj.nombre !== undefined) return String(obj.nombre);
  if (Array.isArray(obj.elements) && obj.elements.length > 0)
    return (obj.elements as unknown[]).map((e: any) => e?.texte ?? String(e)).join("\n");
  // Fallback : collecter les chaines de texte trouvees — jamais de JSON brut
  const strings = Object.values(obj).filter(
    (x): x is string => typeof x === "string" && x !== "",
  );
  return strings.join("\n");
}

function _envelopper(draft: string, type0: string): Record<string, unknown> | null {
  if (type0 === "T") return { texte: draft };
  if (type0 === "N") {
    const n = Number(draft.replace(",", ".").trim());
    if (draft.trim() === "" || isNaN(n)) return null;
    return { nombre: n };
  }
  if (type0 === "L") {
    return { elements: draft.split("\n").map((s) => s.trim()).filter(Boolean) };
  }
  return { texte: draft };
}

function _tempsRelatif(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "a l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const j = Math.floor(h / 24);
  return `il y a ${j}j`;
}

function _parseProvenance(v: CprjValeur | undefined): { prenom: string; fromDiscussion: boolean; time: string } | null {
  if (!v) return null;
  const src = v.source ?? "";
  const fromDiscussion = src.startsWith("cristallisation:");
  let prenom = "";
  if (fromDiscussion) {
    const botCode = src.split(":")[1] ?? "";
    prenom = BOT_PRENOM[botCode] ?? "CarlOS";
  } else if (v.auteur_type === "bot") {
    prenom = "CarlOS";
  }
  if (!prenom) return null;
  return {
    prenom,
    fromDiscussion,
    time: v.created_at ? _tempsRelatif(v.created_at) : "",
  };
}

// ── Markdown leger (meme rendu que DiscussionWindow) ─────────────────────────

function _formatMd(text: string): string {
  if (!text) return "";
  const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = esc.split("\n");
  const out: string[] = [];
  let inList: "ul" | "ol" | null = null;
  const closeList = () => { if (inList) { out.push(`</${inList}>`); inList = null; } };
  for (const line of lines) {
    const t = line.trim();
    if (!t) { closeList(); continue; }
    if (/^[-=─━]{3,}$/.test(t)) { closeList(); out.push('<hr class="my-2 border-gray-200">'); continue; }
    const hm = t.match(/^(#{1,4})\s+(.+)/);
    if (hm) {
      closeList();
      const cls = hm[1].length <= 2 ? "font-semibold text-gray-900" : "font-medium text-gray-700";
      out.push(`<div class="${cls} mt-1 mb-0.5">${applyInlineFmt(hm[2])}</div>`);
      continue;
    }
    const bm = t.match(/^[-*•]\s+(.+)/);
    if (bm) {
      if (inList !== "ul") { closeList(); out.push('<ul class="list-disc list-inside space-y-0.5">'); inList = "ul"; }
      out.push(`<li>${applyInlineFmt(bm[1])}</li>`);
      continue;
    }
    const nm = t.match(/^(\d+)[.)]\s+(.+)/);
    if (nm) {
      if (inList !== "ol") { closeList(); out.push('<ol class="list-decimal list-inside space-y-0.5">'); inList = "ol"; }
      out.push(`<li>${applyInlineFmt(nm[2])}</li>`);
      continue;
    }
    closeList();
    out.push(`<p class="my-0.5">${applyInlineFmt(t)}</p>`);
  }
  closeList();
  return out.join("");
}

// ── Synthese : tronquer avant le separateur d'enrichissement ─────────────────

function _tronquerSynthese(text: string): string {
  if (!text) return "";
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (/^-{3,}$/.test(t) || /^#{1,4}\s+Enrichissement/i.test(t)) {
      return lines.slice(0, i).join("\n").trim();
    }
  }
  return text;
}

// ── Extraction structured_data depuis une valeur cahier ──────────────────────

function _getStructuredData(v: CprjValeur | undefined): Record<string, unknown> | null {
  if (!v?.valeur || typeof v.valeur !== "object" || v.valeur === null) return null;
  const obj = v.valeur as Record<string, unknown>;
  const sd = obj.structured_data;
  if (sd && typeof sd === "object" && !Array.isArray(sd)) return sd as Record<string, unknown>;
  return null;
}

// ── Sous-composant : NiveauBadge ──────────────────────────────────────────────

// Couleur du badge : l'état « approuvée » prend le vert de l'ancien VALIDE (DEC-BADGE-NIVEAU-1).
function cleCouleur(niveau: string, etat?: string | null): string {
  return etat === "approuvee" ? "VALIDE" : niveau;
}

function NiveauBadge({ niveau, provenance, etat }: { niveau: string; provenance?: string | null; etat?: string | null }) {
  const color = NIVEAU_COLORS[cleCouleur(niveau, etat)] ?? NIVEAU_COLORS.DEFAUT;
  const libelle = provenance && etat ? `${PROVENANCE_LIBELLES[provenance] ?? provenance} · ${ETAT_LIBELLES[etat] ?? etat}` : niveau;
  return (
    <span className={cn("text-[9px] font-bold px-1.5 py-0.5 rounded", color)}>
      {libelle}
    </span>
  );
}

// ── Sous-composant : ChampCard ────────────────────────────────────────────────

interface ChampCardProps {
  champ: CprjChamp;
  valeur: CprjValeur | undefined;
  idDispo: boolean;
  isNew: boolean;
  onSave: (champId: string, valeur: Record<string, unknown>) => Promise<void>;
  onError: (msg: string) => void;
  onSendPrompt: (prompt: string) => void;
  activeBotCode: string;
}

function ChampCard({ champ, valeur, idDispo, isNew, onSave, onError, onSendPrompt, activeBotCode }: ChampCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [highlight, setHighlight] = useState(isNew);
  const type0 = champ.types[0] ?? "T";
  const editable = TYPES_EDITABLES.has(type0) && idDispo;
  const rawText = _extraire(valeur, type0);
  const structuredData = _getStructuredData(valeur);
  const provenance = _parseProvenance(valeur);
  const niveau = valeur?.niveau ?? "DEFAUT";
  const couleur = cleCouleur(niveau, valeur?.etat);
  const valObj = valeur?.valeur as Record<string, unknown> | null | undefined;
  const isEnriched = !!valObj && typeof valObj === "object" && "bloc_id" in valObj;
  const displayText = isEnriched ? _tronquerSynthese(rawText) : rawText;

  useEffect(() => {
    if (isNew) {
      setHighlight(true);
      const t = setTimeout(() => setHighlight(false), 5000);
      return () => clearTimeout(t);
    }
  }, [isNew]);

  const handleEdit = () => {
    setDraft(rawText);
    setEditing(true);
  };

  const handleSave = async () => {
    const wrapped = _envelopper(draft, type0);
    if (wrapped === null) {
      onError(`${champ.id} — valeur numerique invalide : "${draft}"`);
      return;
    }
    await onSave(champ.id, wrapped);
    setEditing(false);
  };

  // ── Carte champ (rempli ou vide) ──
  return (
    <div
      className={cn(
        "rounded-xl shadow-sm hover:shadow-md border border-gray-200 bg-white overflow-hidden transition-all duration-500 border-l-4",
        NIVEAU_BORDER[couleur] ?? NIVEAU_BORDER.DEFAUT,
        highlight && "ring-2 ring-amber-300",
      )}
      style={highlight ? { animation: "cahierSlideIn 0.4s ease-out" } : undefined}
    >
      {/* Header band */}
      <div className={cn(
        "flex items-center gap-2 px-3 py-2",
        NIVEAU_HEADER_BG[couleur] ?? NIVEAU_HEADER_BG.DEFAUT,
      )}>
        <FileText className={cn("h-3.5 w-3.5 shrink-0", THEME.iconColor)} />
        <span className="text-sm font-semibold text-gray-800 flex-1 leading-snug">
          {champ.libelle}
        </span>
        {(rawText || structuredData) && <NiveauBadge niveau={niveau} provenance={valeur?.provenance} etat={valeur?.etat} />}
        {editable && !editing && (
          <button
            onClick={handleEdit}
            className="text-xs text-blue-600 hover:underline shrink-0 cursor-pointer"
          >
            modifier
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-3 py-2.5 space-y-2">
        {editing ? (
          <div className="space-y-1.5">
            <textarea
              className="w-full text-sm border border-blue-300 rounded-lg px-2.5 py-2 resize-none focus:ring-2 focus:ring-blue-200 focus:outline-none"
              rows={type0 === "L" ? 4 : 2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                type0 === "N" ? "Nombre (ex. 42 ou 3.5)" :
                type0 === "L" ? "Un element par ligne" : ""
              }
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="text-xs bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
              >
                enregistrer
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 cursor-pointer"
              >
                annuler
              </button>
            </div>
          </div>
        ) : structuredData ? (
          <>
            {displayText && (
              <div
                className="text-sm text-gray-700 leading-relaxed break-words [&>p]:my-0.5 [&>ul]:my-1 [&>ol]:my-1"
                dangerouslySetInnerHTML={{ __html: _formatMd(displayText) }}
              />
            )}
            <BlockDisplayContext.Provider value={{ compact: true, primaryBotCode: "" }}>
              <div className="text-sm text-gray-700 leading-relaxed [&>div>:first-child]:hidden [&>div>:last-child]:hidden">
                <BlockRenderer
                  block={{
                    id: `cahier-${champ.id}`,
                    type: ((valeur as any)?.block_type ?? CHAMP_TO_BLOCK_TYPE[champ.id] ?? "diagnostic") as WorkspaceBlock["type"],
                    title: champ.libelle,
                    summary: "",
                    structured_data: structuredData,
                    credo_step: "C",
                    confidence: 1,
                    source: "CEOB",
                    sourceType: "chat",
                    timestamp: Date.now(),
                  }}
                  onAction={() => {}}
                />
              </div>
            </BlockDisplayContext.Provider>
          </>
        ) : displayText ? (
          type0 === "L" && !isEnriched ? (
            <ul className="text-sm text-gray-700 space-y-0.5 list-disc list-inside leading-relaxed">
              {displayText.split("\n").filter(Boolean).map((el, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: applyInlineFmt(el) }} />
              ))}
            </ul>
          ) : (
            <div
              className="text-sm text-gray-700 leading-relaxed break-words [&>p]:my-0.5 [&>ul]:my-1 [&>ol]:my-1"
              dangerouslySetInnerHTML={{ __html: _formatMd(displayText) }}
            />
          )
        ) : (
          <p className="text-xs text-gray-400 italic">—</p>
        )}

        {/* Provenance line */}
        {provenance && !editing && (
          <div className="flex items-center gap-1.5 pt-1 border-t border-gray-100">
            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
              <span className="text-[10px] font-bold text-gray-500">
                {provenance.prenom[0]}
              </span>
            </div>
            <span className="text-xs text-gray-400">
              {provenance.prenom}
              {provenance.fromDiscussion && " \u00b7 tire de la discussion"}
              {provenance.time && ` \u00b7 ${provenance.time}`}
            </span>
          </div>
        )}

        {/* FieldActionButtons */}
        <FieldActionButtons
          fieldId={champ.id}
          fieldLabel={champ.libelle}
          fieldValue={rawText}
          sectionId={champ.section}
          botCode={activeBotCode}
          onSendPrompt={onSendPrompt}
          onEditInline={handleEdit}
        />
      </div>
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function CahierLivrable({
  onBack,
  discussionId,
  chantierId: chantierId_prop,
  projetId,
  missionId,
  source,
  discussionElements,
  onConfirmerSujet,
  onPreciserSujet,
  onApprouver,
  onValider,
  onRetenirPiste,
  onEpinglerPlan,
  onTransformerEnPistes,
}: Props) {
  const isDiscussion = source === "discussion";

  const [vue, setVue] = useState<Vue>("RS");
  const [structure, setStructure] = useState<CprjStructure | null>(null);
  const [valeurs, setValeurs] = useState<ValeurMap>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionActive, setSectionActive] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [chantierId, setChantierId] = useState<number | null>(chantierId_prop ?? null);
  const [recentlyChanged, setRecentlyChanged] = useState<Set<string>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const prevValeursRef = useRef<ValeurMap>(new Map());
  const prevFetchDepsRef = useRef("");

  const { sendMessage } = useChatContext();
  const { activeBotCode } = useAmorcer();

  // ── Discussion mode: derive structure + valeurs from discussionElements ───
  const discStructure = useMemo(() => {
    if (!isDiscussion || !discussionElements?.structure) return null;
    return {
      sections: discussionElements.structure.sections as unknown as Record<string, CprjSection>,
      champs: discussionElements.structure.champs as unknown as CprjChamp[],
      vues: {} as Record<string, string[]>,
    } satisfies CprjStructure;
  }, [isDiscussion, discussionElements?.structure]);

  const discValeurs = useMemo<ValeurMap>(() => {
    if (!isDiscussion) return new Map();
    return _pontToValeurMap(discussionElements?.valeurs);
  }, [isDiscussion, discussionElements?.valeurs]);

  // Discussion mode: derive participants from avis
  const discParticipants = useMemo(() => {
    if (!isDiscussion || !discussionElements) return [];
    const botSet = new Set<string>(["CEOB"]);
    const collect = (avis: Array<{ bot: string }>) => {
      for (const a of avis) if (a.bot) botSet.add(a.bot);
    };
    collect(discussionElements.avis_sans_cible ?? []);
    for (const f of discussionElements.faits ?? []) collect(f.avis ?? []);
    for (const q of discussionElements.questions ?? []) collect(q.avis ?? []);
    for (const p of discussionElements.pistes ?? []) collect(p.avis ?? []);
    for (const d of discussionElements.decisions ?? []) collect(d.avis ?? []);
    return Array.from(botSet).map(code => ({ code, prenom: BOT_PRENOM[code] ?? code }));
  }, [isDiscussion, discussionElements]);

  // Discussion mode: vue globale counts
  const discVueGlobale = useMemo(() => {
    if (!isDiscussion || !discussionElements) return null;
    return [
      ["Faits", discussionElements.faits?.length ?? 0],
      ["Questions", discussionElements.questions?.length ?? 0],
      ["Pistes", discussionElements.pistes?.length ?? 0],
      ["Decisions", discussionElements.decisions?.length ?? 0],
      ["Actions", discussionElements.actions?.length ?? 0],
      ["Documents", discussionElements.documents?.length ?? 0],
    ] as [string, number][];
  }, [isDiscussion, discussionElements]);

  const recharger = useCallback(() => setTick((t) => t + 1), []);

  // Inject hero CSS animations
  useEffect(() => {
    injectHeroStyles();
    if (!document.getElementById("cahier-slide-in-css")) {
      const s = document.createElement("style");
      s.id = "cahier-slide-in-css";
      s.textContent = "@keyframes cahierSlideIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}";
      document.head.appendChild(s);
    }
  }, []);

  // ── Discussion mode: set initial section + skip CPRJ effects ──────────────
  useEffect(() => {
    if (!isDiscussion) return;
    const sections = discStructure?.sections;
    if (sections) {
      const first = Object.keys(sections)[0] ?? null;
      setSectionActive(first);
    }
  }, [isDiscussion, discStructure]);

  // Auto-resolve chantierId from discussion (DEMO.CAHIER.LIVE.1)
  useEffect(() => {
    if (isDiscussion) return;
    if (chantierId_prop != null) { setChantierId(chantierId_prop); return; }
    if (!discussionId) { setChantierId(null); return; }
    let cancelled = false;
    const jwt = (() => { try { return localStorage.getItem("ghostx-jwt"); } catch { return null; } })();
    const key = import.meta.env.VITE_API_KEY || "missing-key";
    const h: Record<string, string> = {
      "X-API-Key": key,
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    };
    fetch(`/api/v1/discussions/by-external/${encodeURIComponent(discussionId)}`, { headers: h })
      .then((r) => r.ok ? r.json() as Promise<Record<string, unknown>> : null)
      .then((d) => { if (!cancelled && d?.chantier_id) setChantierId(d.chantier_id as number); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [discussionId, chantierId_prop]);

  // Polling 10s (DEMO.CAHIER.LIVE.1) — CPRJ only
  useEffect(() => {
    if (isDiscussion) return;
    const id = setInterval(recharger, 10_000);
    return () => clearInterval(id);
  }, [recharger, isDiscussion]);

  // ── Chargement structure + valeurs (CPRJ only) ────────────────────────────

  useEffect(() => {
    if (isDiscussion) return;
    let cancelled = false;
    // Distinguer un changement de deps (vue, ids) d'un simple tick de polling
    const fetchDeps = `${vue}|${chantierId ?? ""}|${projetId ?? ""}|${missionId ?? ""}`;
    const depsChanged = fetchDeps !== prevFetchDepsRef.current;
    prevFetchDepsRef.current = fetchDeps;
    // Loading visible seulement au premier chargement ou sur changement de vue/ids
    if (depsChanged) setLoading(true);
    setError(null);

    const portees: CprjParams[] = [{ portee: "compte" }];
    if (chantierId) portees.push({ portee: "dossier", chantierId });
    if (projetId) portees.push({ portee: "projet", projetId });
    if (missionId) portees.push({ portee: "soumission", missionId });

    Promise.all([
      getStructure(vue),
      Promise.all(portees.map((p) => getValeurs(p))),
    ])
      .then(([s, vLists]) => {
        if (cancelled) return;
        const newMap = _latestValeurs(vLists.flat());
        const prev = prevValeursRef.current;
        // Detect newly changed fields
        const changed = new Set<string>();
        for (const [champId, val] of newMap) {
          const old = prev.get(champId);
          if (!old || old.version < val.version) changed.add(champId);
        }
        // Skip re-render si donnees identiques (stabilise le polling)
        if (changed.size === 0 && newMap.size === prev.size && prev.size > 0 && !depsChanged) {
          return;
        }
        prevValeursRef.current = newMap;
        setStructure(s);
        if (changed.size > 0 && prev.size > 0) {
          setRecentlyChanged(changed);
          setTimeout(() => setRecentlyChanged(new Set()), 5000);
        }
        setValeurs(newMap);
        setSectionActive((prevSec) => {
          const ids = Object.keys(s.sections);
          const vueIds = s.vues[vue] ?? [];
          const visible = ids.filter((sId) =>
            s.champs.some((c) => c.section === sId && vueIds.includes(c.id)),
          );
          if (prevSec && visible.includes(prevSec)) return prevSec;
          const firstFilled = visible.find((sId) =>
            s.champs.some((c) => c.section === sId && vueIds.includes(c.id) && _champRempli(c, newMap)),
          );
          return firstFilled ?? visible[0] ?? null;
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [vue, chantierId, projetId, missionId, tick]);

  // ── Disponibilite d'identifiant par portee ────────────────────────────────

  function idDisponible(portee: string): boolean {
    if (portee === "compte")     return true;
    if (portee === "dossier")    return chantierId != null;
    if (portee === "projet")     return projetId   != null;
    if (portee === "soumission") return missionId  != null;
    return false;
  }

  // ── Ecriture d'un champ ───────────────────────────────────────────────────

  const handleSave = useCallback(
    async (champId: string, valeur: Record<string, unknown>) => {
      const champ = structure?.champs.find((c) => c.id === champId);
      if (!champ) return;
      if (!idDisponible(champ.portee)) {
        setError(`${champId} — identifiant manquant pour la portee "${champ.portee}"`);
        return;
      }
      try {
        await putValeur(champId, {
          portee: champ.portee,
          valeur,
          niveau: "DECLARE",
          source: "ui",
          chantier_id: champ.portee === "dossier"    ? (chantierId ?? null) : null,
          projet_id:   champ.portee === "projet"     ? (projetId   ?? null) : null,
          mission_id:  (champ.portee as string) === "soumission" ? (missionId  ?? null) : null,
        });
        recharger();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [structure, chantierId, projetId, missionId, recharger],
  );

  // ── Derives ───────────────────────────────────────────────────────────────

  const vueChampIds = structure?.vues[vue] ?? [];

  const champsSection = structure
    ? structure.champs.filter(
        (c) => c.section === sectionActive && vueChampIds.includes(c.id),
      )
    : [];

  const compteurs = structure
    ? Object.fromEntries(
        Object.keys(structure.sections).map((sId) => {
          const champs = structure.champs.filter(
            (c) => c.section === sId && vueChampIds.includes(c.id),
          );
          const remplis = champs.filter((c) => _champRempli(c, valeurs)).length;
          return [sId, { total: champs.length, remplis }];
        }),
      )
    : {};

  // Only sections that have at least 1 champ in this vue
  const visibleSectionIds = structure
    ? Object.keys(structure.sections).filter((sId) => (compteurs[sId]?.total ?? 0) > 0)
    : [];
  const sectionIndex = sectionActive ? visibleSectionIds.indexOf(sectionActive) : 0;
  const totalChamps = visibleSectionIds.reduce((s, sId) => s + (compteurs[sId]?.total ?? 0), 0);
  const champsRemplis = visibleSectionIds.reduce((s, sId) => s + (compteurs[sId]?.remplis ?? 0), 0);

  // ── Rendu mode Discussion (early return — zero risque CPRJ) ──────────────

  if (isDiscussion) {
    const ds = discStructure;
    const dv = discValeurs;
    const couv = discussionElements?.couverture ?? {};

    const dSectionIds = ds ? Object.keys(ds.sections) : [];
    const dSectionActive = sectionActive && dSectionIds.includes(sectionActive)
      ? sectionActive
      : dSectionIds[0] ?? null;

    const dChamps = ds
      ? ds.champs.filter((c: CprjChamp) => c.section === dSectionActive)
      : [];

    const dSectionIndex = dSectionActive ? dSectionIds.indexOf(dSectionActive) : 0;
    const couvSection = dSectionActive ? couv[dSectionActive] : null;
    const activeEtape = ETAPES_DISCUSSION.find(e => ETAPE_TO_SECTION[e.lettre] === dSectionActive) ?? ETAPES_DISCUSSION[0];

    return (
      <div className="max-w-5xl mx-auto px-4 py-4 pb-12 space-y-3">

        {/* Banniere sujet (B-D00.01) */}
        {discussionElements?.sujet && (
          <SujetBanner
            sujet={discussionElements.sujet}
            onConfirmer={onConfirmerSujet}
            onPreciser={onPreciserSujet}
          />
        )}

        {/* Hero compact — maquette Main.dc.html L24-27 */}
        <div className="relative w-full rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden flex items-center px-4 py-3 md:px-6 md:py-4">
          <div className="absolute rounded-full blur-[100px] opacity-60 bg-blue-100/70" style={{ top: '-50%', left: '-10%', width: '50%', height: '200%' }} />
          <div className="absolute rounded-full blur-[120px] opacity-50 bg-sky-100/40" style={{ bottom: '-50%', right: '10%', width: '60%', height: '200%' }} />
          <div className="absolute inset-0 bg-pattern-grid opacity-[0.35]" />
          <div className="relative z-20 flex items-center gap-2 md:gap-4 w-full">
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-1 -ml-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5 text-gray-600" />
            </button>
            <FileText className="h-6 w-6 md:h-7 md:w-7 text-blue-500 shrink-0 stroke-[2]" />
            <h2 className="text-base md:text-lg font-extrabold text-gray-900 shrink-0">
              {activeEtape
                ? `${activeEtape.numero}. ${activeEtape.label} · ${activeEtape.sousTitre}`
                : "Discussion"}
            </h2>
            <div className="flex-1" />
            <span className="text-xs font-bold text-gray-900 shrink-0">
              {activeEtape ? `Étape ${activeEtape.numero} de 5` : ""}
            </span>
            {couvSection && (
              <div className="w-28 h-2 bg-blue-100 rounded-full overflow-hidden shrink-0">
                <div
                  className="h-full bg-blue-400 rounded-full transition-all duration-500"
                  style={{ width: couvSection.total > 0 ? `${(couvSection.remplis / couvSection.total) * 100}%` : '0%' }}
                />
              </div>
            )}
          </div>
        </div>

        {ds ? (
          <div className="flex gap-4 relative">

            {/* Backdrop mobile */}
            {drawerOpen && (
              <div
                className="fixed inset-0 bg-black/20 z-40 md:hidden"
                onClick={() => setDrawerOpen(false)}
              />
            )}

            {/* Sidebar 3 parties : Participants, Étapes, Vue globale */}
            {/* Mobile: tiroir glissant depuis la gauche. Desktop: sidebar statique. */}
            <nav className={cn(
              "pt-2 space-y-1 overflow-y-auto",
              "fixed left-0 top-0 h-full w-[260px] bg-white z-50 shadow-xl px-4 transition-transform duration-200",
              drawerOpen ? "translate-x-0" : "-translate-x-full",
              "md:relative md:translate-x-0 md:w-[180px] md:h-auto md:shadow-none md:px-0 md:z-auto md:bg-transparent md:shrink-0",
            )}>

              {/* Bouton fermer — mobile seulement */}
              <button
                onClick={() => setDrawerOpen(false)}
                className="md:hidden absolute top-3 right-3 p-1 rounded-lg hover:bg-gray-100 cursor-pointer"
                aria-label="Fermer le menu"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>

              {/* ── Participants ── */}
              <div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 mb-0.5">
                  <Users className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Participants</span>
                </div>
                {discParticipants.map(p => (
                  <div key={p.code} className="flex items-center gap-1.5 px-2.5 py-1">
                    <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                      <span className="text-[9px] font-bold text-gray-500">{p.prenom[0]}</span>
                    </div>
                    <span className="text-[10px] text-gray-700">{p.prenom}</span>
                  </div>
                ))}
              </div>

              <div className="h-px bg-gray-100 mx-2 my-2" />

              {/* ── Étapes CREDO ── */}
              <div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 mb-0.5">
                  <ListOrdered className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Etapes</span>
                </div>
                {ETAPES_DISCUSSION.map(etape => {
                  const secId = ETAPE_TO_SECTION[etape.lettre];
                  const active = secId === dSectionActive;
                  const c = couv[secId];
                  const couverte = c?.statut === "couverte";
                  const enCours = c?.statut === "en_cours";
                  return (
                    <button
                      key={etape.lettre}
                      onClick={() => { setSectionActive(secId); setDrawerOpen(false); }}
                      className={cn(
                        "w-full px-2.5 py-1.5 rounded-lg text-left transition-all cursor-pointer",
                        active ? "bg-blue-50 border border-blue-200 shadow-sm" : "hover:bg-gray-50 border border-transparent",
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        {couverte
                          ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                          : <span className={cn(
                              "w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 text-[9px] font-bold",
                              active ? "bg-blue-500 text-white" : "border border-gray-300 text-gray-400",
                            )}>
                              {etape.numero}
                            </span>
                        }
                        <span className={cn("text-[10px] font-bold flex-1 leading-tight", active ? "text-blue-700" : "text-gray-700")}>
                          {etape.label}
                        </span>
                        {c && c.total > 0 && !couverte && (
                          <span className={cn(
                            "text-[10px] px-1 py-0.5 rounded-full font-medium",
                            enCours ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-400",
                          )}>
                            {c.remplis}/{c.total}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="h-px bg-gray-100 mx-2 my-2" />

              {/* ── Vue globale ── */}
              <div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 mb-0.5">
                  <BarChart3 className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Vue globale</span>
                </div>
                {discVueGlobale ? (
                  <div className="px-2.5 space-y-0.5">
                    {discVueGlobale.filter(([, n]) => n > 0).length > 0
                      ? discVueGlobale.filter(([, n]) => n > 0).map(([label, count]) => (
                          <div key={label} className="flex items-center justify-between text-[10px]">
                            <span className="text-gray-600">{label}</span>
                            <span className="font-bold text-gray-700">{count}</span>
                          </div>
                        ))
                      : <span className="text-[10px] text-gray-400 italic px-0">Aucun element</span>
                    }
                  </div>
                ) : (
                  <span className="text-[10px] text-gray-400 italic px-2.5">Chargement...</span>
                )}
              </div>

            </nav>

            {/* Zone contenu */}
            <div className={cn(SF.content, "relative")}>

              {/* E3 — "Ce qu'on sait" (B-D01.01 faits) — maquette Main.dc.html L28-30 */}
              {isDiscussion && (() => {
                const faitsCv = dv.get("B-D01.01");
                const faitsItems = (faitsCv as any)?.elements as Array<{ greffier_id: number; texte: string; valeur: number | null; unite: string | null; niveau: string }> | null;
                if (!faitsItems || faitsItems.length === 0) return null;
                const numFacts = faitsItems.filter(f => f.niveau === "DECLARE" && f.valeur != null);
                const otherFacts = faitsItems.filter(f => !(f.niveau === "DECLARE" && f.valeur != null));
                return (
                  <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden hover:shadow-md hover:border-blue-200 transition-all">
                    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-[#00B4D8]/10">
                      <Target className="h-4 w-4 text-gray-900 stroke-[2.5]" />
                      <span className="text-sm font-bold text-gray-900">Ce qu&apos;on sait</span>
                      <span className="text-xs font-bold bg-gray-900 text-white px-2.5 py-0.5 rounded-full ml-auto">{faitsItems.length} fait{faitsItems.length > 1 ? "s" : ""}</span>
                    </div>
                    <div className="px-4 py-3 space-y-3">
                      {numFacts.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 text-center">
                          {numFacts.map((f, i) => (
                            <div key={f.greffier_id ?? i}>
                              <p className="text-xs text-gray-400">{f.texte}</p>
                              <p className={cn("text-xs font-bold", FACT_NUM_COLORS[i % FACT_NUM_COLORS.length])}>{f.valeur}{f.unite ? ` ${f.unite}` : ""}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {otherFacts.length > 0 && (
                        <ul className="text-sm text-gray-700 space-y-0.5 list-disc list-inside leading-relaxed">
                          {otherFacts.map((f, i) => (
                            <li key={f.greffier_id ?? i} className="text-xs">{f.texte}{f.valeur != null ? ` (${f.valeur}${f.unite ? ` ${f.unite}` : ""})` : ""}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </section>
                );
              })()}

              {/* E4 — "Points à cerner" (B-D01.02 questions) — maquette Main.dc.html L31-47 */}
              {isDiscussion && (() => {
                const qCv = dv.get("B-D01.02");
                const qItems = (qCv as any)?.elements as Array<{ greffier_id: number; texte: string; statut: string; niveau: string }> | null;
                if (!qItems || qItems.length === 0) return null;
                return (
                  <div className="grid grid-cols-2 gap-3">
                    {qItems.map((q, i) => {
                      const qs = Q_CARD_STYLES[i % Q_CARD_STYLES.length];
                      return (
                        <div key={q.greffier_id ?? i} className={cn("rounded-xl overflow-hidden border-2 shadow-sm hover:shadow-md transition-all bg-gradient-to-b to-white", qs.border, qs.from)}>
                          <div className={cn("px-3 py-2 flex items-center justify-between", qs.hdr)}>
                            <span className="text-xs text-gray-900 font-bold line-clamp-1">{q.texte}</span>
                            <span className={cn("px-2.5 py-1 rounded-full text-xs font-bold shrink-0 ml-2", qs.badge)}>À cerner</span>
                          </div>
                          <div className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <span className="w-5 h-5 text-[9px] bg-blue-600 rounded-full text-white font-bold flex items-center justify-center shrink-0">C</span>
                              <span className="text-[10px] text-gray-500 font-medium">CarlOS</span>
                              <button
                                onClick={() => sendMessage(q.texte)}
                                className={cn("text-[10px] px-3 py-1.5 rounded-full font-bold ml-auto shadow-sm cursor-pointer", qs.btn)}
                              >
                                Poser la question
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {dChamps.length === 0 ? null : (
                dChamps.filter((c: CprjChamp) => !(isDiscussion && (c.id === "B-D01.01" || c.id === "B-D01.02"))).map((champ: CprjChamp) => {
                  const cv = dv.get(champ.id);
                  const elemId = (cv as any)?.id as number | undefined;
                  const sec = champ.section;
                  const showEpingler = onEpinglerPlan && elemId && elemId > 0 && sec >= "D01" && sec <= "D03";
                  const showRetenir = onRetenirPiste && elemId && elemId > 0 && champ.id === "B-D02.T01";
                  const showTransformer = onTransformerEnPistes && elemId && elemId > 0
                    && (champ.id === "B-D02.T02" || champ.id === "B-D02.T03");
                  const hasGeste = !!(showEpingler || showRetenir || showTransformer);
                  return (
                    <div key={champ.id}>
                      <ChampCard
                        champ={champ}
                        valeur={cv}
                        idDispo={false}
                        isNew={false}
                        onSave={async () => {}}
                        onError={() => {}}
                        onSendPrompt={(prompt) => sendMessage(prompt)}
                        activeBotCode={activeBotCode}
                      />
                      {hasGeste && (
                        <div className="flex gap-1.5 mt-1 px-1">
                          {showRetenir && (
                            <button
                              onClick={() => onRetenirPiste!(elemId!)}
                              className="text-[10px] px-2.5 py-1 rounded-full border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 font-medium cursor-pointer transition-colors flex items-center gap-1"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> Retenir
                            </button>
                          )}
                          {showEpingler && (
                            <button
                              onClick={() => onEpinglerPlan!(elemId!)}
                              className="text-[10px] px-2.5 py-1 rounded-full border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 font-medium cursor-pointer transition-colors flex items-center gap-1"
                            >
                              Epingler au plan
                            </button>
                          )}
                          {showTransformer && (
                            <button
                              onClick={() => onTransformerEnPistes!([elemId!])}
                              className="text-[10px] px-2.5 py-1 rounded-full border border-amber-300 bg-white text-amber-700 hover:bg-amber-50 font-medium cursor-pointer transition-colors flex items-center gap-1"
                            >
                              Transformer en pistes
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

          </div>
        ) : (
          <div className="flex items-center gap-2 py-8 justify-center text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Chargement...</span>
          </div>
        )}

      </div>
    );
  }

  // ── Rendu CPRJ ─────────────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 pb-12 space-y-3">

      {/* Bouton retour */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-[10px] text-gray-500 hover:text-gray-700 transition-colors cursor-pointer mb-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Retour au chantier
        </button>
      )}

      {/* Onglets vue */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {VUES.map((v) => (
          <button
            key={v}
            onClick={() => setVue(v)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer",
              vue === v
                ? "bg-gray-900 text-white shadow-sm"
                : "text-gray-500 hover:bg-gray-200 hover:text-gray-700",
            )}
          >
            {VUE_LABELS[v]}
          </button>
        ))}
      </div>

      {/* Banniere erreur */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
          <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <span className="text-xs text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-[10px] text-red-500 hover:text-red-700 cursor-pointer"
          >
            x
          </button>
        </div>
      )}

      {/* Hero compact */}
      <div className="relative w-full rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden flex items-center px-6 py-4">
        <div className={cn("absolute rounded-full blur-[100px] opacity-60", THEME.heroBlur1)} style={{ top: '-50%', left: '-10%', width: '50%', height: '200%' }} />
        <div className={cn("absolute rounded-full blur-[120px] opacity-50", THEME.heroBlur2)} style={{ bottom: '-50%', right: '10%', width: '60%', height: '200%' }} />
        <div className="absolute inset-0 bg-pattern-grid opacity-[0.35]" />
        <div className="relative z-20 flex items-center gap-4 w-full">
          <FileText className={cn("h-7 w-7 shrink-0 stroke-[2]", THEME.iconColor)} />
          <h2 className="text-lg font-extrabold text-gray-900 shrink-0">
            {sectionActive && structure?.sections[sectionActive]
              ? structure.sections[sectionActive].title
              : VUE_LABELS[vue]}
          </h2>
          <div className="flex-1" />
          <span className="text-xs font-bold text-gray-900 shrink-0">
            Section {sectionIndex + 1} de {visibleSectionIds.length}
          </span>
          <div className={cn("w-28 h-2 rounded-full overflow-hidden shrink-0", THEME.progressBg)}>
            <div
              className={cn("h-full rounded-full transition-all duration-500", THEME.progressFill)}
              style={{ width: totalChamps > 0 ? `${(champsRemplis / totalChamps) * 100}%` : '0%' }}
            />
          </div>
        </div>
      </div>

      {/* Spinner initial */}
      {loading && !structure ? (
        <div className="flex items-center gap-2 py-8 justify-center text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Chargement...</span>
        </div>
      ) : structure ? (
        <div className="flex gap-4">

          {/* Sidebar */}
          <nav className={SF.sidebarW}>
            {Object.entries(structure.sections)
              .filter(([sId]) => visibleSectionIds.includes(sId))
              .map(([sId, sec]) => {
              const active = sId === sectionActive;
              const c = compteurs[sId] ?? { total: 0, remplis: 0 };
              const allDone = c.total > 0 && c.remplis === c.total;
              return (
                <button
                  key={sId}
                  onClick={() => setSectionActive(sId)}
                  className={cn(SF.btnBase, active ? SF.btnActive : SF.btnInactive)}
                >
                  {active
                    ? <CheckCircle2 className={SF.iconActive} />
                    : <div className="w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0" />
                  }
                  <span className={cn("flex-1 min-w-0", active ? SF.labelActive : SF.labelInactive)}>
                    {sec.title}
                  </span>
                  {c.total > 0 && (
                    <span className={cn(
                      "text-[10px] px-1 py-0.5 rounded-full font-medium",
                      allDone ? "bg-emerald-100 text-emerald-600" : "bg-amber-50 text-amber-600",
                    )}>
                      {allDone ? "\u2713" : "\u2026"}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Zone contenu */}
          <div className={cn(SF.content, "relative")}>
            {/* Indicateur polling — sans decalage de mise en page */}
            {loading && structure && (
              <div className="absolute top-1 right-1 flex items-center gap-1 text-[10px] text-gray-300 z-10">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              </div>
            )}

            {champsSection.length === 0 ? (
              <div className={cn("border-l-[3px] pl-3 py-2", THEME.borderLeft)}>
                <p className="text-xs text-gray-400 italic">
                  Aucun champ pour cette section dans la vue {VUE_LABELS[vue]}.
                </p>
              </div>
            ) : (
              champsSection.map((champ) => (
                <ChampCard
                  key={champ.id}
                  champ={champ}
                  valeur={valeurs.get(champ.id)}
                  idDispo={idDisponible(champ.portee)}
                  isNew={recentlyChanged.has(champ.id)}
                  onSave={handleSave}
                  onError={setError}
                  onSendPrompt={(prompt) => sendMessage(prompt)}
                  activeBotCode={activeBotCode}
                />
              ))
            )}

            {/* Carte de fin */}
            {totalChamps > 0 && champsRemplis === totalChamps && (
              <div className="mt-4">
                <div className={CARD_BASE}>
                  <div className={cn(CARD_HEADER, "px-6 py-4")}>
                    <div className="flex items-center justify-center gap-4 mb-3">
                      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", THEME.bgLight)}>
                        <FileText className={cn("h-4 w-4 stroke-[2.5]", THEME.iconColor)} />
                      </div>
                      <ArrowLeft className="h-4 w-4 text-gray-400 rotate-180" />
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 stroke-[2.5]" />
                      </div>
                    </div>
                    <p className="text-sm font-bold text-gray-900 text-center">Cahier complet</p>
                    <p className="text-xs text-gray-500 text-center mt-1">
                      {totalChamps} champs remplis — pret pour validation
                    </p>
                  </div>
                  <div className="px-6 py-3 flex gap-2 justify-center flex-wrap">
                    {onBack && (
                      <button
                        type="button"
                        onClick={onBack}
                        className="text-xs bg-white text-gray-700 px-4 py-2 rounded-full font-bold border border-gray-300 cursor-pointer hover:bg-gray-50 flex items-center gap-1.5"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" /> Retour au chantier
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      ) : null}

    </div>
  );
}
